import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../../services/api';
import { FiTruck, FiCalendar, FiMapPin, FiClock, FiCheckCircle, FiAlertCircle, FiPlus, FiArrowRight, FiDollarSign, FiTrendingUp, FiFilter } from 'react-icons/fi';

/** Devis avec mission « active » mais souvent sans ligne `moves` (flux app / négociation). */
const QUOTE_ACTIVE_FOR_MOVES_LIST = new Set([
  'accepted',
  'in_progress',
  'collection_completed',
  'offer_accepted',
  'scheduled',
]);

function mergeQuotesIntoMoves(movesArr, quotesArr) {
  const list = Array.isArray(movesArr) ? movesArr.filter(Boolean).map((m) => ({ ...m })) : [];
  const seenQuoteIds = new Set(
    list.map((m) => m.quote_id || m.quoteId).filter(Boolean).map((id) => String(id)),
  );
  const qArr = Array.isArray(quotesArr) ? quotesArr : [];
  for (const q of qArr) {
    if (!q?.id) continue;
    const st = String(q.status || '').toLowerCase();
    if (!QUOTE_ACTIVE_FOR_MOVES_LIST.has(st)) continue;
    const qid = String(q.id);
    if (seenQuoteIds.has(qid)) continue;
    seenQuoteIds.add(qid);
    list.push({
      id: q.id,
      quote_id: q.id,
      _fromQuote: true,
      move_date: q.move_date,
      date: q.move_date,
      from_address: q.from_address,
      to_address: q.to_address,
      from_city: q.from_city,
      to_city: q.to_city,
      status: q.status,
      price_cents: q.price_cents,
      created_at: q.created_at,
      first_name: null,
      last_name: null,
    });
  }
  return list;
}

/** Mappe statut devis / mission vers les onglets de l’UI (pending | in_progress | completed). */
function mapStatusForFilter(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return 'completed';
  if (s === 'in_progress' || s === 'collection_completed') return 'in_progress';
  if (
    s === 'accepted' ||
    s === 'offer_accepted' ||
    s === 'scheduled' ||
    s === 'pending' ||
    s === 'confirmed'
  ) {
    return 'pending';
  }
  return s;
}

export default function ClientMoves() {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'in_progress', 'completed'
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMoves = async () => {
      try {
        setLoading(true);
        const [moveRows, quotesRes] = await Promise.all([
          api.getClientMoves().catch(() => []),
          api.getClientQuotes().catch(() => null),
        ]);
        const movesArr = Array.isArray(moveRows) ? moveRows : [];
        const quotesRaw = Array.isArray(quotesRes) ? quotesRes : quotesRes?.data;
        const quotesArr = Array.isArray(quotesRaw) ? quotesRaw : [];
        setMoves(mergeQuotesIntoMoves(movesArr, quotesArr));
      } catch (err) {
        console.error('Erreur lors de la récupération des déménagements:', err);
        setError('Impossible de charger vos déménagements. Veuillez réessayer.');
        setMoves([]); // S'assurer que moves est un tableau vide en cas d'erreur
      } finally {
        setLoading(false);
      }
    };

    fetchMoves();
  }, []);

  const handleNewMove = () => {
    navigate('/client/search');
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    const statusMap = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      in_progress: 'En cours',
      collection_completed: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
      accepted: 'Accepté',
      offer_accepted: 'Offre acceptée',
      scheduled: 'Planifié',
    };

    const statusClass = {
      pending: 'bg-amber-500/20 text-amber-400',
      confirmed: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-indigo-500/20 text-indigo-400',
      collection_completed: 'bg-indigo-500/20 text-indigo-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      accepted: 'bg-green-500/20 text-green-400',
      offer_accepted: 'bg-green-500/20 text-green-400',
      scheduled: 'bg-blue-500/20 text-blue-400',
    };

    return (
      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusClass[s] || 'bg-gray-500/20 text-gray-400'}`}>
        {statusMap[s] || status}
      </span>
    );
  };

  // Filtrer les déménagements (statuts devis inclus : accepted → onglet En attente)
  const filteredMoves =
    filter === 'all' ? moves : moves.filter((m) => mapStatusForFilter(m?.status) === filter);

  // Variantes d'animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const getProgressPercentage = (status) => {
    const s = String(status || '').toLowerCase();
    const progress = {
      pending: 25,
      confirmed: 50,
      accepted: 45,
      offer_accepted: 50,
      scheduled: 55,
      in_progress: 75,
      collection_completed: 80,
      completed: 100,
      cancelled: 0,
    };
    return progress[s] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="flex items-center justify-center gap-3 text-gray-300 bg-gray-800 p-8 rounded-2xl border border-gray-700">
          <div className="animate-spin h-8 w-8 border-3 border-gray-600 border-t-amber-400 rounded-full"></div>
          <span className="text-lg">Chargement de vos déménagements...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-6 py-4 rounded-2xl text-center">
          <FiAlertCircle className="w-6 h-6 mx-auto mb-2" />
          {error}
        </div>
      </div>
    );
  }

  // Calculs de statistiques
  const totalMoves = Array.isArray(moves) ? moves.length : 0;
  const inProgressMoves = Array.isArray(moves)
    ? moves.filter((m) => mapStatusForFilter(m?.status) === 'in_progress').length
    : 0;
  const completedMoves = Array.isArray(moves)
    ? moves.filter((m) => mapStatusForFilter(m?.status) === 'completed').length
    : 0;
  const pendingMoves = Array.isArray(moves)
    ? moves.filter((m) => mapStatusForFilter(m?.status) === 'pending').length
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="relative overflow-hidden bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-3xl p-8 border border-amber-500/20 shadow-2xl"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-yellow-500/5"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <motion.div variants={itemVariants} className="space-y-4">
                <div className="flex items-center gap-3">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <FiTruck className="w-6 h-6 text-gray-900" />
                  </motion.div>
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-white bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
                      Mes déménagements
                    </h1>
                    <p className="text-amber-400 font-medium flex items-center gap-2">
                      <FiTrendingUp className="w-4 h-4" />
                      Gérez tous vos déménagements en un seul endroit
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="mt-6 lg:mt-0 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-amber-800/20 to-amber-600/20 rounded-xl p-4 border border-amber-500/20 hover:border-amber-400/40 transition-all cursor-pointer shadow-lg"
                  onClick={() => setFilter('all')}
                >
                  <div className="text-2xl lg:text-3xl font-bold text-amber-400">{totalMoves}</div>
                  <div className="text-gray-400 text-sm">Total</div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-blue-800/20 to-blue-600/20 rounded-xl p-4 border border-blue-500/20 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg"
                  onClick={() => setFilter('pending')}
                >
                  <div className="text-2xl lg:text-3xl font-bold text-blue-400">{pendingMoves}</div>
                  <div className="text-gray-400 text-sm">En attente</div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-indigo-800/20 to-indigo-600/20 rounded-xl p-4 border border-indigo-500/20 hover:border-indigo-400/40 transition-all cursor-pointer shadow-lg"
                  onClick={() => setFilter('in_progress')}
                >
                  <div className="text-2xl lg:text-3xl font-bold text-indigo-400">{inProgressMoves}</div>
                  <div className="text-gray-400 text-sm">En cours</div>
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-green-800/20 to-green-600/20 rounded-xl p-4 border border-green-500/20 hover:border-green-400/40 transition-all cursor-pointer shadow-lg"
                  onClick={() => setFilter('completed')}
                >
                  <div className="text-2xl lg:text-3xl font-bold text-green-400">{completedMoves}</div>
                  <div className="text-gray-400 text-sm">Terminés</div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <FiFilter className="w-5 h-5 text-gray-400" />
            <span className="text-gray-400 text-sm">Filtre:</span>
            <div className="flex gap-2">
              {['all', 'pending', 'in_progress', 'completed'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                    filter === f
                      ? 'bg-amber-500 text-gray-900'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : f === 'in_progress' ? 'En cours' : 'Terminés'}
                </button>
              ))}
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNewMove}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold rounded-xl transition-all shadow-lg"
          >
            <FiPlus className="w-5 h-5 mr-2" />
            Nouveau déménagement
          </motion.button>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-6"
        >
          {filteredMoves.length > 0 ? (
            filteredMoves.map((move, index) => (
              <motion.div
                key={move._fromQuote ? `quote-${move.quote_id || move.id}` : move.id}
                variants={itemVariants}
                className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all shadow-xl"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-4 mb-6">
                        <motion.div 
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className="flex-shrink-0"
                        >
                          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border border-amber-500/30 shadow-lg">
                            <FiTruck className="w-8 h-8 text-amber-400" />
                          </div>
                        </motion.div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                            <h3 className="text-xl font-bold text-white truncate">
                              {(move.from_city || move.from_address || move.fromAddress || 'Départ')} 
                              <span className="mx-2 text-amber-400">→</span> 
                              {(move.to_city || move.to_address || move.toAddress || 'Arrivée')}
                            </h3>
                            {getStatusBadge(move.status)}
                          </div>
                          
                          <div className="w-full bg-gray-700 rounded-full h-3 mb-2 overflow-hidden">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${getProgressPercentage(move.status)}%` }}
                              transition={{ duration: 1, delay: index * 0.1 }}
                              className="bg-gradient-to-r from-amber-500 to-yellow-500 h-3 rounded-full shadow-lg"
                            ></motion.div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-400">
                            <span>Progression</span>
                            <span className="font-semibold text-amber-400">{getProgressPercentage(move.status)}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                        <motion.div 
                          whileHover={{ scale: 1.02 }}
                          className="flex items-center gap-3 p-4 bg-gray-700/50 rounded-xl border border-gray-600 hover:border-amber-500/30 transition-all"
                        >
                          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FiCalendar className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-medium">Date</p>
                            <p className="text-sm font-semibold text-white">
                              {(() => {
                                const raw = move.move_date || move.date || move.scheduledDate;
                                if (!raw) return '—';
                                const d = new Date(raw);
                                return Number.isNaN(d.getTime())
                                  ? '—'
                                  : d.toLocaleDateString('fr-FR', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                    });
                              })()}
                            </p>
                          </div>
                        </motion.div>
                        
                        <motion.div 
                          whileHover={{ scale: 1.02 }}
                          className="flex items-center gap-3 p-4 bg-gray-700/50 rounded-xl border border-gray-600 hover:border-amber-500/30 transition-all"
                        >
                          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FiMapPin className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 font-medium">Distance</p>
                            <p className="text-sm font-semibold text-white">
                              {move.distance_km ? `${move.distance_km} km` : '—'}
                            </p>
                          </div>
                        </motion.div>
                        
                        {move.first_name || move.last_name ? (
                          <motion.div 
                            whileHover={{ scale: 1.02 }}
                            className="flex items-center gap-3 p-4 bg-blue-500/10 rounded-xl border border-blue-500/30 hover:border-blue-400/50 transition-all"
                          >
                            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FiCheckCircle className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-xs text-blue-400 font-medium">Déménageur</p>
                              <p className="text-sm font-semibold text-white truncate">
                                {move.first_name} {move.last_name}
                              </p>
                            </div>
                          </motion.div>
                        ) : (
                          <motion.div 
                            whileHover={{ scale: 1.02 }}
                            className="flex items-center gap-3 p-4 bg-amber-500/10 rounded-xl border border-amber-500/30 hover:border-amber-400/50 transition-all"
                          >
                            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FiClock className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                              <p className="text-xs text-amber-400 font-medium">Statut</p>
                              <p className="text-sm font-semibold text-white">En attente</p>
                            </div>
                          </motion.div>
                        )}
                      </div>

                      {(move.total_cost || move.price_cents) && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="mt-4 p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FiDollarSign className="w-5 h-5 text-green-400" />
                              <span className="text-sm font-semibold text-green-400">Prix convenu</span>
                            </div>
                            <span className="text-2xl font-bold text-white">
                              {move.total_cost 
                                ? `${move.total_cost.toLocaleString('fr-TN')} TND`
                                : move.price_cents 
                                  ? `${(move.price_cents/100).toLocaleString('fr-TN')} TND`
                                  : '—'}
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </div>

                    <div className="flex-shrink-0 flex flex-col gap-3 mt-6 lg:mt-0">
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          move._fromQuote
                            ? navigate(`/client/quotes/${move.quote_id || move.id}`)
                            : navigate(`/client/moves/${move.id}`)
                        }
                        className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg"
                      >
                        <FiArrowRight className="w-5 h-5" />
                        Voir détails
                      </motion.button>
                      
                      {move.status === 'in_progress' && (
                        <motion.div
                          animate={{ opacity: [1, 0.7, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="px-4 py-2 bg-indigo-500/10 text-indigo-400 rounded-lg text-sm font-semibold text-center border border-indigo-500/20"
                        >
                          🚚 En cours de déménagement
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-12 text-center shadow-xl"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="mx-auto w-32 h-32 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full flex items-center justify-center mb-6 border border-amber-500/20"
              >
                <FiTruck className="w-16 h-16 text-amber-500/50" />
              </motion.div>
              <h3 className="text-2xl font-bold text-white mb-3">Aucun déménagement</h3>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                {filter === 'all' 
                  ? "Commencez par créer votre premier déménagement pour suivre vos trajets et organiser vos déplacements."
                  : `Aucun déménagement avec le statut "${filter === 'pending' ? 'en attente' : filter === 'in_progress' ? 'en cours' : 'terminé'}"`}
              </p>
              {filter === 'all' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleNewMove}
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold rounded-xl transition-all shadow-lg"
                >
                  <FiPlus className="w-6 h-6 mr-3" />
                  Créer un déménagement
                </motion.button>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}